import { createRef, Component } from "react";
import { extract, EventAggregator, CartContextConsumer, GhostButton, LanguageSelector } from "@parksandresorts/core";
import { Link } from "gatsby";
import styles from "./main-navigation.module.less";
import { graphql } from "gatsby";
import Cart from "components/block-elements/cart/cart.js";
import { GlobalContext } from "components/stores/global-context";
import EventAttributeStore from "components/stores/event-attribute-store";
import LunrSearch from "components/block-elements/search/lunr-search.tsx";
import LinkWrapper from "components/block-elements/link-wrapper/link-wrapper";
import LoginLink from "components/block-elements/login-link/login-link";
import UiPicture from "@rnb/ui-picture";

const topBannerHeight = 56;
const yPosStartFade = 300;
const yPosEndFade = 350;
const relevantYPositions = [0, topBannerHeight, yPosStartFade, yPosEndFade];

function calculateSetFixed(yPos) {
  return yPos > topBannerHeight;
}

function calculateNavigationFadeInPercentage(yPos, prefillNavigation) {
  if (prefillNavigation) {
    return 100;
  }

  const percentageCompleted = ((yPos - yPosStartFade) / (yPosEndFade - yPosStartFade)) * 100;
  return Math.max(0, Math.min(percentageCompleted, 100));
}

export default class MainNavigation extends Component {
  constructor(props) {
    super(props);

    this.menuIconTitle = extract(props, "navigation.menuIconTitle", "");
    this.logoTransparentImage = extract(this.props, "navigation.logoTransparent.image");
    this.logoFilledImage = extract(this.props, "navigation.logoFilled.image");
    this.logoTransparentUrl = extract(this.props, "navigation.logoTransparent.internalLink.slug");
    this.logoFilledUrl = extract(this.props, "navigation.logoFilled.internalLink.slug");
    this.searchNoResultText = extract(this.props, "navigation.searchNoResultText", "");
    this.searchPlaceholderLabel = extract(this.props, "navigation.searchPlaceholderLabel", "");
    this.searchResultHeader = extract(this.props, "navigation.searchResultHeader", "");
    this.searchResultBackground = extract(this.props, "navigation.searchResultBackground");
    this.linkGroups = extract(props, "navigation.linkGroups");
    this.logo = extract(props, "siteConfig.footerLogo");
    this.loginEnabled = extract(props, "siteConfig.enableMyPages", false);

    this.languageSelectionList = extract(props, "navigation.languageSelectionList", []);

    this.onTransitionEndCollapsedElement = this.onTransitionEndCollapsedElement.bind(this);

    this.state = {
      yPos: 0,
    };

    this.ref_searchComponent = createRef();
  }

  componentDidMount() {
    this.onOpenMenuSubscription = EventAggregator.subscribe("onToggleMenu", this.openMenuEvent);

    this._isMounted = true;

    setTimeout(() => {
      this.onOrientationChangeSubscription = EventAggregator.subscribe("onOrientationChange", (e) =>
        this.onOrientationChange(e),
      );
      this.onScrollSubscription = EventAggregator.subscribe("onScroll", (yPos) => {
        this.onScroll(yPos);
      });
    }, 25);
  }

  openMenuEvent = (e) => {
    this.toggleMore();
  };

  componentWillUnmount() {
    if (this.onOrientationChangeSubscription) {
      this.onOrientationChangeSubscription.remove();
    }

    if (this.onScrollSubscription) {
      this.onScrollSubscription.remove();
    }

    if (this.onOpenMenuSubscription) {
      this.onOpenMenuSubscription.remove();
    }

    this._isMounted = false;
  }

  onOrientationChange() {
    setTimeout(() => {
      if (this._isMounted) {
        this.onScroll();
      }
    }, 500);
  }

  onScroll(yPos) {
    const minRelevantYPos = Math.min(...relevantYPositions);
    const maxRelevantYPos = Math.max(...relevantYPositions);

    // optimization to avoid unnecessary re-renders
    yPos = Math.max(minRelevantYPos, Math.min(yPos, maxRelevantYPos));
    if (yPos !== this.state.yPos) {
      this.setState({
        yPos,
      });
    }
  }

  toggleCart() {
    const showCart = !this.context.showCart;
    this.context.setValue(
      {
        showCart: showCart,
        showMenu: false,
        showSearch: false,
        isAnimating: true,
        isAnimationDone: false,
      },
      () => {
        if (!showCart) {
          this.context.setValue({ isMainVisible: true });
        }
      },
    );
  }

  toggleMore() {
    const showMenu = !this.context.showMenu;
    this.context.setValue(
      {
        showMenu: showMenu,
        showCart: false,
        showSearch: false,
        isAnimating: true,
        isAnimationDone: false,
      },
      () => {
        if (!showMenu) {
          this.context.setValue({ isMainVisible: true });
        }
      },
    );
  }

  closeMenu() {
    this.context.setValue({
      showMenu: false,
      showCart: false,
      showSearch: false,
      isMainVisible: true,
    });
  }

  toggleSearch() {
    const showSearch = !this.context.showSearch;
    this.context.setValue(
      {
        showSearch: showSearch,
        showCart: false,
        isAnimating: true,
        isAnimationDone: false,
      },
      () => {
        this.ref_searchComponent.focus();
        if (!showSearch) {
          this.context.setValue({ isMainVisible: true });
        }
      },
    );
  }

  setTotalItemsInCart(cartContext) {
    if (typeof window !== "undefined" && (cartContext.isInitialCartFetch === null || cartContext.isInitialCartFetch)) {
      const cartState = cartContext;

      if (cartState && cartState.totalItems) {
        return cartState.totalItems;
      }
    }

    return cartContext.totalItems ? cartContext.totalItems : 0;
  }

  onTransitionEndCollapsedElement() {
    this.setAnimationDone();

    if (!this.context.isMainVisible) {
      return;
    }

    if (
      this.context.showMenu ||
      this.context.showSearch ||
      (this.context.showCart && EventAttributeStore.getCurrentWindowWidth() < 767)
    ) {
      this.context.setValue({ isMainVisible: false });
    }
  }

  setAnimationDone() {
    this.context.setValue({
      isAnimating: false,
      isAnimationDone: true,
    });
  }

  onClickLogo() {
    this.context.setValue({ isMainVisible: true, showMenu: false, showSearch: false });
  }

  getLogoPicture(img, width, id) {
    return {
      id: `${this.props.contentful_id}_${id}`,
      src: `${img.file.url}?w=${width}&q=70`,
      alt: extract(img, "description", ""),
      loading: "lazy",
      decode: "async",
      width: extract(img, "file.details.image.width", undefined),
      height: extract(img, "file.details.image.height", undefined),
      sources: [{ srcset: `${img.file.url}?w=${width}&q=70, ${img.file.url}?w=${width * 2}&q=70 2x` }],
    };
  }

  render() {
    const navigationFadeInPercentage = calculateNavigationFadeInPercentage(
      this.state.yPos,
      this.props.prefillNavigation,
    );
    const isFixed = !this.props.isShowingTopBanner || calculateSetFixed(this.state.yPos);

    const opacityLayerStyle = {
      opacity: navigationFadeInPercentage / 100,
    };

    const transparentLogoStyle = {
      opacity: (100 - navigationFadeInPercentage) / 100,
    };

    return (
      <nav className={isFixed ? "" : styles.mainNavigationAbsolute}>
        <div
          className={[
            styles.mainNavigation,
            navigationFadeInPercentage > 50 ? styles.mainNavigation_filled : "",
            isFixed ? "" : styles.mainNavigationAbsolute,
          ].join(" ")}
        >
          <div style={opacityLayerStyle} className={styles.opacityLayer}></div>
          <div className={styles.innerWrapper}>
            <div className={styles.language}>
              <LanguageSelector locales={this.languageSelectionList?.languages ?? []} />
            </div>
            <div className={styles.search}>
              <GhostButton
                forwardRef={(ref) => {
                  this.ref_searchIconBtn = ref;
                }}
                onClick={() => this.toggleSearch()}
                label="Sök"
                labelClass={styles.btnLabel}
                iconCenterNew="search"
                className={["waves-effect", styles.searchBtn, styles.menuBtn].join(" ")}
              />
            </div>
            <div className={styles.logoWrapper}>
              <LinkWrapper url={this.logoTransparentUrl}>
                {this.logoTransparentImage ? (
                  <UiPicture
                    {...this.getLogoPicture(this.logoTransparentImage, 96, "logo")}
                    style={transparentLogoStyle}
                    className={styles.logo}
                  />
                ) : (
                  ""
                )}
                {this.logoFilledImage ? (
                  <UiPicture
                    {...this.getLogoPicture(this.logoFilledImage, 96, "filled_logo")}
                    style={opacityLayerStyle}
                    className={styles.logo}
                  />
                ) : (
                  ""
                )}
              </LinkWrapper>
            </div>
            <CartContextConsumer>
              {(cartContext) => (
                <div className={styles.rightWrapper}>
                  <div key={cartContext.cartUpdatedKey} className={styles.cartIconWrapper}>
                    {cartContext.totalItems > 0 && (
                      <GhostButton
                        className={["waves-effect", styles.menuBtn, this.context.showCart ? styles.show : ""].join(" ")}
                        onClick={() => {
                          this.toggleCart();
                          EventAggregator.publish("trackEvent", {
                            category: "Navigation",
                            action: "Opened",
                            label: "Cart",
                          });
                        }}
                        iconCenterNew="cart"
                      />
                    )}
                    {cartContext.totalItems > 0 && (
                      <span
                        key={cartContext.cartUpdatedKey + "top"}
                        className={[
                          styles.itemsInCart,
                          cartContext.isInitialCartFetch === null || cartContext.isInitialCartFetch
                            ? ""
                            : styles.addItem,
                        ].join(" ")}
                      >
                        {this.setTotalItemsInCart(cartContext)}
                      </span>
                    )}
                  </div>

                  <GhostButton
                    className={["waves-effect", styles.menuBtn].join(" ")}
                    onClick={() => {
                      this.toggleMore();
                      EventAggregator.publish("trackEvent", {
                        category: "Navigation",
                        action: "Opened",
                        label: "Menu",
                      });
                    }}
                    label={this.menuIconTitle ? this.menuIconTitle : undefined}
                    labelClass={styles.btnLabel}
                    iconCenterNew="menu"
                  />
                </div>
              )}
            </CartContextConsumer>
          </div>
        </div>

        <div
          onTransitionEnd={this.onTransitionEndCollapsedElement}
          className={[
            styles.collapsedMoreWrapper,
            this.context.showMenu ? styles.show : "",
            this.context.showMenu && this.context.isAnimationDone ? styles.menuShowAnimationDone : "",
            this.context.isAnimating && !this.context.showMenu ? styles.closeAnimation : "",
          ].join(" ")}
        >
          <GhostButton
            className={[styles.closeBtn, styles.menuBtn, "waves-effect"].join(" ")}
            onClick={() => {
              this.toggleMore();
              EventAggregator.publish("trackEvent", { category: "Navigation", action: "Closed", label: "Menu" });
            }}
            iconCenterNew="close"
          />
          {this.logo && (
            <LinkWrapper
              onPreNavigate={this.onClickLogo.bind(this)}
              url={this.props.isStartPage ? undefined : this.logoTransparentUrl}
            >
              <UiPicture {...this.getLogoPicture(this.logo, 70, "moreMenuLogo_1")} className={styles.moreMenuLogo} />
            </LinkWrapper>
          )}
          {this.loginEnabled && (
            <div className={[styles.menuSection, styles.loginSection].join(" ")}>
              <LoginLink />
            </div>
          )}
          {this.linkGroups &&
            this.linkGroups.map((linkGroup) => {
              return (
                <div key={linkGroup.id} className={styles.menuSection}>
                  {linkGroup.headline && (
                    <h4 className={[styles.groupHeadline, "body-s"].join(" ")}> {linkGroup.headline} </h4>
                  )}
                  <ul className={styles.moreLinksList}>
                    {linkGroup.listObjects instanceof Array &&
                      linkGroup.listObjects.map((listItem) => {
                        return (
                          <Link
                            key={listItem.id}
                            className={styles.moreLinksItemUrl}
                            activeClassName={styles.active}
                            to={listItem.internalLink && listItem.internalLink.slug}
                            onClick={() => {
                              this.closeMenu();
                              EventAggregator.publish("trackEvent", {
                                category: "Navigation",
                                action: "Clicked link",
                                label: listItem.title && listItem.title,
                              });
                            }}
                          >
                            <li className={[styles.moreLinksItem, linkGroup.secondary ? "body-s" : "h5"].join(" ")}>
                              {listItem.title && listItem.title}
                            </li>
                          </Link>
                        );
                      })}
                  </ul>
                </div>
              );
            })}
        </div>
        <div
          onTransitionEnd={this.onTransitionEndCollapsedElement}
          className={[
            styles.cartWrapper,
            this.context.showCart ? styles.show : "",
            this.context.showCart && this.context.isAnimationDone ? styles.cartShowAnimationDone : "",
            this.context.isAnimating && !this.context.showCart ? styles.closeAnimation : "",
          ].join(" ")}
        >
          <div className={styles.cartInnerWrapper}>
            <GhostButton
              className={[styles.closeBtn, styles.menuBtn, "waves-effect"].join(" ")}
              onClick={() => {
                this.toggleCart();
                EventAggregator.publish("trackEvent", { category: "Navigation", action: "Closed", label: "Cart" });
              }}
              iconCenterNew="close"
            />
            <Cart />
          </div>
          <div
            aria-label="Close overlay"
            className={styles.overlay}
            tabIndex={0}
            role="button"
            onKeyDown={() => this.toggleCart()}
            onClick={() => this.toggleCart()}
          />
        </div>
        <div
          onTransitionEnd={this.onTransitionEndCollapsedElement}
          className={[
            styles.collapsedSearchWrapper,
            this.context.showSearch ? styles.show : "",
            this.context.showSearch && this.context.isAnimationDone ? styles.searchShowAnimationDone : "",
            this.context.isAnimating && !this.context.showSearch ? styles.closeAnimation : "",
          ].join(" ")}
        >
          <GhostButton
            className={[styles.closeBtn, styles.menuBtn, "waves-effect"].join(" ")}
            onClick={() => this.toggleSearch()}
            iconCenterNew="close"
          />
          {this.logo && (
            <LinkWrapper
              onPreNavigate={this.onClickLogo.bind(this)}
              url={this.props.isStartPage ? undefined : this.logoTransparentUrl}
            >
              <UiPicture {...this.getLogoPicture(this.logo, 70, "moreMenuLogo_2")} className={styles.moreMenuLogo} />
            </LinkWrapper>
          )}
          <div className="block--max-width-narrow">
            <LunrSearch
              ref={(ref) => {
                this.ref_searchComponent = ref;
              }}
              searchResultHeading={this.searchResultHeader}
              searchPlaceHolder={this.searchPlaceholderLabel}
              noSearchResultText={this.searchNoResultText}
            />
          </div>
        </div>
      </nav>
    );
  }
}

MainNavigation.contextType = GlobalContext;

export const fragmentMainNavigation = graphql`
  fragment MainNavigation on ContentfulMainNavigation {
    __typename
    id
    menuIconTitle
    linkGroups {
      id
      headline
      secondary
      listObjects {
        __typename
        ... on ContentfulLink {
          id
          title
          internalLink {
            slug
          }
        }
      }
    }
    logoTransparent {
      image {
        ...Asset
      }
      internalLink {
        slug
      }
    }
    logoFilled {
      image {
        ...Asset
      }
      internalLink {
        slug
      }
    }
    searchPlaceholderLabel
    searchResultHeader
    searchNoResultText
    languageSelectionList {
      languages {
        url
        title
        locale
      }
    }
  }
`;
